import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import infoSmall from '~/assets/icons/info_small.svg';
import setup from '~/assets/icons/setup.svg';
import star from '~/assets/icons/star.svg';
import trash from '~/assets/icons/trash.svg';
import user from '~/assets/icons/user.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { rage } from '~/rage';
import { useModal } from '../../hooks';
import {
	setSaveAbsoluteIndex,
	setSaveSelectedIndex,
	setSaveSelectedList,
	useLayoutDispatch,
} from '../../redux';
import { ISaveProps, popupPosition, popups, popupText } from './Save.model';
import {
	StyledButtonsContainer,
	StyledContainer,
	StyledInfoHintAuthorContainer,
	StyledInfoHintContainer,
	StyledInfoHintRowContainer,
	StyledMode,
	StyledPopupContainer,
	StyledPopupWrapper,
	StyledTextLabel,
} from './Save.styled';

const Save: React.FC<
	ISaveProps & {
		index: number;
		id: string;
		scrollOffset?: number;
		absoluteIndex: number;
	}
> = ({
	name,
	info,
	author,
	creationDate,
	isFavorite,
	list,
	index,
	id,
	mode,
	absoluteIndex,
}: ISaveProps & {
	index: number;
	id: string;
	absoluteIndex: number;
}) => {
	const dispatch = useLayoutDispatch();
	const containerRef = useRef<HTMLDivElement>(null);

	const [hover, setHover] = useState<boolean>(false);
	const [popupVisible, setPopupVisible] = useState<boolean>(false);
	const [infoHintVisible, setInfoHintVisible] = useState<boolean>(false);
	const [activePopup, setActivePopup] = useState<popups>(popups.Upload);
	const [elementPosition, setElementPosition] = useState({
		top: 0,
		left: 0,
		center: 0,
		buttonWidth: 0,
		popupLeft: 0,
		infoHintLeft: 0,
	});
	const { handleSetupSaveModal, handleDeleteModal } = useModal();
	const zoomFactor = useZoomFactor();

	const updatePopupPosition = useCallback(() => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			const buttonWidth = rect.width;

			const buttonCenter =
				rect.left * (1 / zoomFactor) + (buttonWidth * (1 / zoomFactor)) / 2;

			setElementPosition({
				top: rect.bottom * (1 / zoomFactor),
				left: rect.left,
				center: buttonCenter,
				buttonWidth: buttonWidth,
				popupLeft: 0,
				infoHintLeft: 0,
			});
		}
	}, [zoomFactor]);

	useEffect(() => {
		if (popupVisible || infoHintVisible) {
			updatePopupPosition();
		}
	}, [popupVisible, infoHintVisible, updatePopupPosition]);

	const handleShowSetupModal = useCallback(() => {
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
		handleSetupSaveModal();
	}, [handleSetupSaveModal, absoluteIndex]);

	const handleShowDeleteModal = useCallback(() => {
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
		handleDeleteModal();
	}, [handleDeleteModal, absoluteIndex]);

	const handlePopupShow = useCallback((popup: popups) => {
		setActivePopup(popup);
		setPopupVisible(true);
	}, []);

	const handleClick = useCallback(() => {
		playSound(Sound.ClickButton);
		dispatch(setSaveSelectedIndex(index));
		dispatch(setSaveSelectedList(list));
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
	}, [index, list, absoluteIndex]);

	const handleSetFavorite = useCallback(() => {
		rage.handlingTuning
			.setPresetFavorite(absoluteIndex, !isFavorite)
			.catch((err) => rage.notifications.showException(err));
	}, [absoluteIndex, isFavorite]);

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					in={popupVisible}
					classNames="popup"
					timeout={500}
					unmountOnExit
				>
					<StyledPopupWrapper>
						<StyledPopupContainer
							$activePopup={activePopup}
							style={{
								top: elementPosition.top - 8,
								left: elementPosition.center - 60 + popupPosition[activePopup],
								position: 'fixed',
							}}
						>
							<Text>{popupText[activePopup]}</Text>
						</StyledPopupContainer>
					</StyledPopupWrapper>
				</CSSTransition>,
				document.body,
			)}
			{createPortal(
				<CSSTransition
					in={infoHintVisible}
					classNames={'hint'}
					timeout={500}
					unmountOnExit
				>
					<StyledPopupWrapper>
						<StyledInfoHintContainer
							style={{
								top: elementPosition.top - 8,
								left: elementPosition.center - 97,
								position: 'fixed',
							}}
						>
							<StyledInfoHintRowContainer>
								<Text
									style={{
										fontSize: '14px',
										fontWeight: 500,
										color: '#000000',
									}}
								>
									{name}
								</Text>
								<StyledMode $mode={mode}>
									<Text>{mode}</Text>
								</StyledMode>
							</StyledInfoHintRowContainer>
							<Divider
								$direction={'H'}
								$color={'#000000'}
								style={{ opacity: 0.1 }}
							/>
							<Text
								style={{
									fontSize: '14px',
									fontWeight: 400,
									color: '#000000',
									opacity: '0.75',
								}}
							>
								{info}
							</Text>
							<StyledInfoHintRowContainer style={{ opacity: 0.5 }}>
								<StyledInfoHintAuthorContainer>
									<img src={user} />
									<Text>{author}</Text>
								</StyledInfoHintAuthorContainer>
								<Text>{creationDate}</Text>
							</StyledInfoHintRowContainer>
						</StyledInfoHintContainer>
					</StyledPopupWrapper>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer
				ref={containerRef}
				onMouseEnter={() => {
					setHover(true);
					playSound(Sound.LinkCursor);
				}}
				onMouseLeave={() => setHover(false)}
				onClick={handleClick}
				id={id}
			>
				<StyledTextLabel $hover={hover}>{name}</StyledTextLabel>
				<StyledButtonsContainer>
					<Button
						height={ButtonHeight.Height30}
						color={isFavorite ? ButtonColors.Select : ButtonColors.Default}
						style={{ fontSize: 16 }}
						onClick={handleSetFavorite}
						onMouseEnter={() => {
							handlePopupShow(popups.Favorite);
						}}
						onMouseLeave={() => setPopupVisible(false)}
						square
					>
						<img src={star} width={14} height={14} />
					</Button>
					<Button
						height={ButtonHeight.Height30}
						color={ButtonColors.Default}
						onMouseEnter={() => {
							setInfoHintVisible(true);
						}}
						onMouseLeave={() => setInfoHintVisible(false)}
						square
					>
						<img src={infoSmall} width={14} height={14} />
					</Button>
					<Button
						height={ButtonHeight.Height30}
						color={ButtonColors.Default}
						onClick={handleShowSetupModal}
						onMouseEnter={() => {
							handlePopupShow(popups.Setup);
						}}
						onMouseLeave={() => setPopupVisible(false)}
						square
					>
						<img src={setup} width={14} height={14} />
					</Button>
					<Button
						height={ButtonHeight.Height30}
						color={ButtonColors.Cancel}
						onMouseEnter={() => {
							handlePopupShow(popups.Delete);
						}}
						onMouseLeave={() => setPopupVisible(false)}
						onClick={handleShowDeleteModal}
						square
					>
						<img src={trash} width={14} height={14} />
					</Button>
				</StyledButtonsContainer>
			</StyledContainer>
		</Fragment>
	);
};

export { Save };
